/**
 * @fileoverview The HR Admin block of the Active Client details.
 * @description The bank's first user and where their invitation stands: name, email, phone, state,
 * who invited them and when, how many invitations went out, and when the latest one expires. The
 * Resend button shows only when the details list RESEND_HR_ADMIN_INVITE; it is held until the
 * invitation's `nextResendAt` (a countdown shows how long) and once no resends remain. Invite HR
 * Admin is the modal's own action, for a client with no HR Admin yet.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import {
  dayOf,
  HR_ADMIN_BADGE_VARIANT,
  hasClientAction,
} from '@/features/sales/lib/sales-clients.utils'
import Badge from '@/shared/components/atoms/Badge'
import Button from '@/shared/components/atoms/Button'
import DetailField from '@/shared/components/molecules/DetailField'
import { useCountdown } from '@/shared/hooks/useCountdown'

import ClientPanel from './ClientPanel'
import SalesRequestError from './SalesRequestError'

import type { ClientDetails } from '@/features/sales/types/sales-client.types'

interface ClientHrAdminSectionProps {
  client: ClientDetails
  onResend: () => void
  isResending: boolean
  /** The failed resend's error, if the last try failed. */
  resendError: Error | null
}

/**
 * @component
 * @param {ClientHrAdminSectionProps} props - The client and the resend wiring.
 * @param {ClientDetails} props.client - The client in full.
 * @param {() => void} props.onResend - Resends the invitation.
 * @param {boolean} props.isResending - Whether a resend is in flight.
 * @param {Error | null} props.resendError - The last resend's error, if it failed.
 */
function ClientHrAdminSection({
  client,
  onResend,
  isResending,
  resendError,
}: ClientHrAdminSectionProps) {
  const { t } = useTranslation()
  const { hrAdmin } = client
  const invitation = hrAdmin?.invitation ?? null
  const waitSeconds = useCountdown(invitation ? Date.parse(invitation.nextResendAt) : null)
  const d = 'sales.activeClients.detail'

  if (!hrAdmin) {
    return (
      <ClientPanel title={t(`${d}.hrAdmin`)} testId="client-detail-hr-admin">
        <p
          className="col-span-full text-compact text-slate"
          data-testid="client-detail-no-hr-admin"
        >
          {t('sales.activeClients.hrAdminNotInvited')}
        </p>
      </ClientPanel>
    )
  }

  const canResend = hasClientAction(client, 'RESEND_HR_ADMIN_INVITE')
  const outOfResends = invitation !== null && invitation.resendsRemaining <= 0

  return (
    <ClientPanel title={t(`${d}.hrAdmin`)} testId="client-detail-hr-admin">
      <DetailField label={t(`${d}.contactPerson`)} data-testid="client-detail-hr-name">
        {hrAdmin.name}
      </DetailField>
      <DetailField label={t(`${d}.hrAdminState`)} data-testid="client-detail-hr-state">
        <Badge variant={HR_ADMIN_BADGE_VARIANT[hrAdmin.state]}>
          {t(`sales.activeClients.hrAdminState.${hrAdmin.state}`)}
        </Badge>
      </DetailField>
      <DetailField label={t(`${d}.email`)} data-testid="client-detail-hr-email">
        {hrAdmin.email}
      </DetailField>
      <DetailField label={t(`${d}.phone`)} data-testid="client-detail-hr-phone">
        {hrAdmin.phone}
      </DetailField>
      <DetailField label={t(`${d}.invitedBy`)} data-testid="client-detail-hr-invited-by">
        {hrAdmin.invitedBy?.name ?? '—'} · {dayOf(hrAdmin.invitedAt)}
      </DetailField>
      <DetailField label={t(`${d}.invitationsSent`)} data-testid="client-detail-hr-sent">
        {hrAdmin.invitationsSent}
      </DetailField>
      {invitation && (
        <DetailField label={t(`${d}.invitationExpires`)} data-testid="client-detail-hr-expires">
          {dayOf(invitation.expiresAt)}
        </DetailField>
      )}
      {canResend && invitation && (
        <div className="col-span-full flex flex-col gap-2">
          <div className="w-fit">
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={onResend}
              isLoading={isResending}
              disabled={waitSeconds > 0 || outOfResends}
              data-testid="client-resend-invite-button"
            >
              {t('sales.activeClients.resend.button')}
            </Button>
          </div>
          {waitSeconds > 0 && !outOfResends && (
            <span className="text-[12px] text-slate" data-testid="client-resend-wait">
              {t('sales.activeClients.resend.wait', { seconds: waitSeconds })}
            </span>
          )}
          {outOfResends && (
            <span className="text-[12px] text-slate" data-testid="client-resend-limit">
              {t('sales.activeClients.resend.limit')}
            </span>
          )}
          {resendError && <SalesRequestError error={resendError} />}
        </div>
      )}
    </ClientPanel>
  )
}

export default ClientHrAdminSection
